import { Box, Button, Typography, CircularProgress } from '@mui/material'
import { filterBenchmarkWhales } from 'containers/dashboard/benchmarkReport/utils/filterBenchmarkWhales'
import WhalesSection from 'containers/dashboard/benchmarkReport/whalesSection/WhalesSection'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { RootState, useAppDispatch } from 'store'
import { actions as reportingBenchmarkActions } from 'store/reporting/benchmark'
import { Whale } from 'store/reporting/benchmark/types'
import useInterval from 'use-interval'

type Props = {
  id: string
  onRegenerate?: () => void
  showExtended?: boolean
}

export default function BenchmarkReport({ id, onRegenerate, showExtended }: Props) {
  const dispatch = useAppDispatch()
  const error = useSelector((state: RootState) => state.reportingBenchmark.error)
  const benchmarkInfo = useSelector((state: RootState) => state.reportingBenchmark.benchmarkInfo)
  const status = useSelector((state: RootState) => state.reportingBenchmark.status)
  const whales: Whale[] | null = useSelector((state: RootState) => state.reportingBenchmark.whales)
  const isWhalesLoading = useSelector((state: RootState) => state.reportingBenchmark.loading.whales)

  const isBenchmarkLoading = status && status !== 'complete'
  const name = benchmarkInfo?.name ?? ''
  const ipcrCodes = benchmarkInfo?.ipcrCodes ?? []
  const ipcrCodesLength = ipcrCodes.length

  // Filter out whales with ci lower than threshold, except the target whale, the one wihtn dn equal to name
  const threshold = ipcrCodesLength === 1 ? 1 : Math.max(2, ipcrCodesLength - 2)

  const benchmarkWhales = filterBenchmarkWhales(whales, name, threshold)
  //create in store reporting benchmark call to fetch benchmark status and data, then display data in this component once ready.

  useEffect(() => {
    if (!id) return
    dispatch(reportingBenchmarkActions.fetchBenchmarkInfo(id))
    dispatch(reportingBenchmarkActions.fetchBenchmarkStatus(id))
  }, [id, dispatch])

  useEffect(() => {
    if (!id) return
    if (status === 'complete') {
      dispatch(reportingBenchmarkActions.fetchBenchmarkData(id))
    }
  }, [status, id, dispatch])

  useInterval(
    () => {
      if (!id) return
      dispatch(reportingBenchmarkActions.fetchBenchmarkStatus(id))
    },
    status === 'complete' || status === 'error' ? null : 5000,
  )

  return (
    <Box m={2}>
      <Typography mb={1}>Status: {status}</Typography>
      {error.fetchStatus && (
        <Typography mb={1} color="error">
          Retrying...
        </Typography>
      )}

      <Typography mb={1}>Name: {name}</Typography>
      <Typography mb={1}>IPCR codes: {ipcrCodes.join(', ')}</Typography>
      <Typography mb={1}>Params: {JSON.stringify(benchmarkInfo?.params)}</Typography>

      {onRegenerate && (
        <Button variant="outlined" sx={{ mt: 2, mr: 2 }} onClick={onRegenerate}>
          Regenerate with new filters
        </Button>
      )}
      <Button
        variant="contained"
        color="primary"
        sx={{ mt: 2 }}
        disabled={status !== 'complete'}
        onClick={() => dispatch(reportingBenchmarkActions.downloadBenchmarkZip(id))}>
        Download zip
      </Button>
      {isWhalesLoading ||
        (isBenchmarkLoading && (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ))}
      {!isBenchmarkLoading && (
        <Box mt={2}>
          {showExtended && (
            <Box mt={2}>
              {whales && (
                <>
                  <Typography variant="h5" mt={4} mb={2}>
                    Whales Section
                  </Typography>
                  <Typography mb={2}>
                    Filtered by ci {'>='} {threshold} (ipcr codes amount − 2, with a minimum of 2; if there is only 1 IPCR code,
                    the threshold is 1)
                  </Typography>
                  <Typography variant="body2" mb={2}>
                    Total whales: {whales.length}. Filtered whales: {benchmarkWhales?.length ?? 0}
                  </Typography>
                </>
              )}
            </Box>
          )}
          {benchmarkWhales && benchmarkWhales.length > 0 && (
            <WhalesSection targetName={name} benchmarkWhales={benchmarkWhales} showExtended={showExtended} />
          )}
        </Box>
      )}
    </Box>
  )
}
